import { Drawer, Input, PageHeader, Space, Descriptions, Divider } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Material } from 'domains/matDomain';
import { useTranslation } from 'react-i18next';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { RevxButton } from 'components/RevxButton';
import { isUndefined } from 'lodash-es';

const { Search } = Input;

const PAGE_SIZE = 10;

const MatBaseSelect = () => {
  const { t } = useTranslation();
  const matBaseSelectVisible = useAppSelector(
    (state) => state.mat.matBaseSelect.visible
  );
  const dispatch = useAppDispatch();

  // table data
  const dataSource = useAppSelector(
    (state) => state?.mat?.matBaseSelect.data?.content || []
  );
  const total = useAppSelector(
    (state) => state.mat.matBaseSelect.data?.totalElements || 0
  );
  const currentPage = useAppSelector(
    (state) => state.mat.matBaseSelect.currentPage
  );
  const loading = useAppSelector(
    (state) => state.loading.effects.mat.getListForBaseSelect
  );

  const onClose = () => {
    setCurItem(undefined);
    dispatch.mat.setMatBaseSelectVisible(false);
    dispatch.mat.setMatBaseSelectCurrentPage(1);
  };

  const onSearch = () => {};

  useEffect(() => {
    dispatch.mat.getListForBaseSelect({
      page: {
        currentPage: currentPage,
        pageSize: PAGE_SIZE,
      },
    });
  }, [currentPage, dispatch.mat]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const curPage = pagination.current;
          dispatch.mat.setMatBaseSelectCurrentPage(curPage);
          break;
      }
    },
    [dispatch.mat]
  );
  const [curItem, setCurItem] = useState<Material>();

  const onSave = async () => {
    if (!isUndefined(curItem)) {
      await dispatch.mat.createFromBase(curItem.id);
      onClose();
    }
  };

  const columns: TableColumnsType<Material> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('platform.mat.table.columns.manufacturer'),
      dataIndex: 'manufacturerName',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.mat.table.columns.stockType'),
      dataIndex: 'stockType',
    },
    {
      title: t('platform.mat.table.columns.category'),
      dataIndex: 'category',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <a key="select" onClick={() => setCurItem(record)}>
            {t('common.select')}
          </a>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      destroyOnClose
      title={t('platform.mat.form.title.create')}
      placement="right"
      width={'800px'}
      closable={true}
      onClose={onClose}
      visible={matBaseSelectVisible}
    >
      <PageHeader
        ghost={false}
        extra={[
          <Search
            key="search"
            placeholder={t('platform.mat.baseSelect.searchName')}
            onSearch={onSearch}
            allowClear
            enterButton
          />,
        ]}
      />
      <RevxTable<Material>
        loading={loading}
        columns={columns}
        className="drawer-table-sticky"
        dataSource={dataSource}
        pagination={{
          pageSize: PAGE_SIZE,
          total: total,
          showSizeChanger: false,
        }}
        onChange={onTableChange}
      />

      {Boolean(curItem) && (
        <>
          <Divider orientation="left">
            {t('platform.mat.baseSelect.selectedDescTitle')}
          </Divider>
          <Descriptions
            bordered
            size="small"
            extra={[
              <Space key="space">
                <RevxButton key="save" type="primary" onClick={onSave}>
                  {t('common.save')}
                </RevxButton>
                <RevxButton onClick={onClose}>{t('common.cancel')}</RevxButton>
              </Space>,
            ]}
          >
            <Descriptions.Item
              label={t('platform.mat.table.columns.manufacturer')}
            >
              {curItem?.manufacturerName || ''}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.name')}>
              {curItem?.name}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('platform.mat.table.columns.stockType')}
            >
              {curItem?.stockType}
            </Descriptions.Item>
            <Descriptions.Item label={t('platform.mat.table.columns.category')}>
              {curItem?.category}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {dayjs(curItem?.createAt).format('YYYY-MM-DD')}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Drawer>
  );
};

export default MatBaseSelect;
